import { memo, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
// import { useTranslate } from '../../../hooks/language/use-translate'
import { useCreateGoldMiner } from '../../../hooks/queries/games/use-create-game-gold-miner'
import { useGetMyProfile } from '../../../hooks/queries/profile/use-get-my-profile'
import { useGlobalStore } from '../../../store/global-store'
import { notifyOpenRecharge } from '../../../commom/rn-bridge'
import closeSvg from '../modal-games/components/images/close.svg'
import { MiningScene } from './components/mining-scene'
import { useGetAppSetting } from '../../../hooks/queries/app/use-get-app-setting'
import { ModalEarning } from './modal-earnings'
import { ModalRanking } from './modal-ranking'
import { ModalRewards } from './modal-rewards'
import { ModalRules } from './modal-rules'
import { ModalHistory } from './modal-history'
import { WoodSign } from './components/wood-sign'

import imgBackground from './images/background-mining.webp'

import { formatDecimalPlaces } from '../../../helpers/functions/format-decimal-places'
import imgButton2 from '../modal-games/components/images/button-2.png'

type OverlayModal = 'rules' | 'rewards' | 'ranking' | 'history' | null

interface Props {
  handleCloseModal: () => void
}

// Tamanho de DESIGN do jogo (aspecto do fundo 976x1104 ≈ 0.884 retrato). O palco
// é sempre renderizado nesse tamanho e ESCALADO com transform pra caber na área
// que o webview der — mesma lógica "contain" que os slots (canvas) usam. Assim o
// layout NÃO depende da altura no banco: em 50%, 70% ou 100% ele mantém a
// proporção, centralizado, sem esticar. Ver games.height do gold miner.
const DESIGN_W = 440
const DESIGN_H = 498

function useFitScale() {
  const containerRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const recompute = () => {
      const { width, height } = el.getBoundingClientRect()
      if (!width || !height) return
      // contain: menor razão entre largura/altura disponíveis e o design
      setScale(Math.min(width / DESIGN_W, height / DESIGN_H))
    }
    recompute()
    const ro = new ResizeObserver(recompute)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  return { containerRef, scale }
}

const PLAYS = [1, 10, 50, 100]

export const ModalGameGoldMiner = memo(({ handleCloseModal }: Props) => {
  const [overlay, setOverlay] = useState<OverlayModal>(null)
  const { containerRef, scale } = useFitScale()
  const coinsInLive = useGlobalStore(useShallow((state) => state.coinsInLive))
  const { appSetting } = useGetAppSetting()
  // preço por jogada vem da config do servidor (apps_settings_games)
  const valueBet = appSetting?.gameGoldMinerValueBet || 0

  const closeOverlay = () => setOverlay(null)

  const {
    gameGoldMinerData,
    isModalEarningVisible,
    isLoading,
    timesBet,
    errorMessage,
    create,
    setGame,
  } = useCreateGoldMiner()

  const { user } = useGetMyProfile()

  // prêmio do 1x fica em cima do monte até a próxima jogada (ou 3s)
  const singlePrize =
    !isLoading && !isModalEarningVisible && timesBet === 1
      ? gameGoldMinerData?.[0] ?? null
      : null

  useEffect(() => {
    if (!singlePrize) return
    const t = setTimeout(() => {
      setGame((prev) => ({ ...prev, gameGoldMinerData: [] }))
    }, 3000)
    return () => clearTimeout(t)
  }, [singlePrize, setGame])

  useEffect(() => {
    if (!errorMessage) return
    const t = setTimeout(() => {
      setGame((prev) => ({ ...prev, errorMessage: null }))
    }, 3500)
    return () => clearTimeout(t)
  }, [errorMessage, setGame])

  const handleCloseModalEarning = () => {
    setGame((prevState) => ({
      ...prevState,
      isModalEarningVisible: false,
      gameGoldMinerData: [],
    }))
  }

  const play = (times: number) => {
    if (isLoading) return
    setGame((prev) => ({ ...prev, gameGoldMinerData: [], timesBet: times }))
    create({ timesBet: times })
  }

  const coins = coinsInLive || user?.coins || 0

  return (
    <div
      ref={containerRef}
      className="flex h-screen w-screen items-end justify-center overflow-hidden bg-transparent"
    >
      {/* palco de tamanho fixo (design) ESCALADO pra caber — como o canvas dos
          slots. Ancorado no BOTTOM: a sobra de espaço fica em cima (transparente),
          o jogo cola na base do webview. transform-origin bottom pra não deslocar. */}
      <div
        style={{
          width: DESIGN_W,
          height: DESIGN_H,
          transform: `scale(${scale})`,
          transformOrigin: 'bottom center',
          flex: '0 0 auto',
        }}
      >
        <div className="relative h-full w-full overflow-hidden rounded-t-2xl select-none">
          {overlay === 'rules' && <ModalRules onClose={closeOverlay} />}
          {overlay === 'rewards' && <ModalRewards onClose={closeOverlay} />}
          {overlay === 'ranking' && <ModalRanking onClose={closeOverlay} />}
          {overlay === 'history' && <ModalHistory onClose={closeOverlay} />}
          <ModalEarning
            visible={isModalEarningVisible}
            isLoading={isLoading}
            gameGoldMinerData={gameGoldMinerData}
            timesBet={timesBet}
            valueBet={valueBet}
            handlePlay={() => play(timesBet)}
            handleCloseModal={handleCloseModalEarning}
          />

          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${imgBackground})` }}
          />

          {/* Topo: saldo + recarga | fechar */}
          <div className="absolute top-3 left-3 z-20 flex items-center gap-1.5 rounded-full border border-yellow-300/70 bg-black/55 py-1 pr-1 pl-2 backdrop-blur-sm">
            <CoinBadge size={16} />
            <span className="text-[13px] font-extrabold text-white tabular-nums">
              {formatDecimalPlaces(coins)}
            </span>
            <button
              type="button"
              aria-label="Recarga"
              onClick={() => notifyOpenRecharge()}
              className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-b from-yellow-300 to-amber-500 text-sm leading-none font-black text-amber-900 active:scale-90"
            >
              +
            </button>
          </div>

          <button
            type="button"
            aria-label="Fechar"
            onClick={handleCloseModal}
            className="absolute top-3 right-3 z-20 flex h-7 w-7 items-center justify-center rounded-full bg-black/45 backdrop-blur-sm"
          >
            <img src={closeSvg} className="h-3.5 w-3.5" />
          </button>

          <MiningScene mining={isLoading} prize={singlePrize} />

          {/* aviso de bloqueio (termo, limite) */}
          {errorMessage && (
            <div className="absolute top-14 right-4 left-4 z-20 rounded-xl border border-red-300/60 bg-red-900/90 px-3 py-2 text-center text-[12px] font-bold text-white shadow-lg">
              {errorMessage}
            </div>
          )}

          {/* Placas: Regras / Prêmios / Ranking / Meus ganhos — arte sem texto,
              rótulo traduzido pelo país do jogador (WoodSign) */}
          <div className="absolute top-[200px] right-1 z-10 flex flex-col items-end gap-1">
            <WoodSign sign="rules" point="left" onClick={() => setOverlay('rules')} />
            <WoodSign sign="rewards" point="right" onClick={() => setOverlay('rewards')} />
            <WoodSign sign="ranking" point="left" onClick={() => setOverlay('ranking')} />
            <WoodSign sign="history" point="right" onClick={() => setOverlay('history')} />
          </div>

          {/* Jogadas — todas juntas embaixo, com o custo */}
          <div className="absolute right-2 bottom-3 left-2 z-10 grid grid-cols-4 gap-1.5">
            {PLAYS.map((times) => (
              <button
                key={times}
                type="button"
                onClick={() => play(times)}
                disabled={isLoading}
                className="relative h-[50px] transition active:scale-95 disabled:opacity-60"
              >
                <img
                  src={imgButton2}
                  alt=""
                  className="pointer-events-none absolute inset-0 h-full w-full object-fill"
                />
                <span className="absolute inset-y-0 right-1 left-[30%] flex flex-col items-center justify-center leading-none text-white drop-shadow-[0_1px_1px_rgba(0,0,0,0.6)]">
                  <span className="text-[15px] font-black">{times}x</span>
                  {valueBet > 0 && (
                    <span className="mt-0.5 flex items-center gap-0.5 text-[10px] font-bold text-yellow-100">
                      <CoinBadge size={9} />
                      {formatDecimalPlaces(times * valueBet)}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
})

function CoinBadge({ size }: { size: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <circle cx="12" cy="12" r="10" fill="#f59e0b" stroke="#d97706" strokeWidth="1.5" />
      <text x="12" y="16" textAnchor="middle" fontSize="11" fontWeight="bold" fill="#78350f">
        $
      </text>
    </svg>
  )
}
